<script setup>
import { nextTick, shallowRef } from "vue";
import UiIcon from "./ui/UiIcon.vue";
import BridgeDialogNavigation from "./map/BridgeDialogNavigation.vue";
import { getWaypointMarkerPresentation } from "../lib/map-helpers";

const waypoints = [
  {
    label: "Turn onto FM 93",
    type: "turn",
    typeLabel: "Turn anchor",
    x: 27.125,
    y: 38.538462,
    mobileX: 27.125,
    mobileY: 43.181818,
  },
  {
    label: "Bridge: FM 93 #090140183502008",
    type: "bridge",
    typeLabel: "Bridge structure",
    comment:
      "Must be the only vehicle on the bridge and drive in the center lane or straddle center line.",
    x: 72.9375,
    y: 61.461538,
    mobileX: 72.875,
    mobileY: 56.727273,
  },
].map((waypoint, index) => ({
  ...waypoint,
  marker: getWaypointMarkerPresentation(waypoint, index + 1, 4),
  position: {
    "--desktop-x": waypoint.x,
    "--desktop-y": waypoint.y,
    "--mobile-x": waypoint.mobileX,
    "--mobile-y": waypoint.mobileY,
  },
}));
const selected = shallowRef(null);
const hovered = shallowRef(null);
const closeButton = shallowRef(null);
function selectWaypoint(waypoint) {
  selected.value = waypoint;
  hovered.value = null;
}
function clearSelection() {
  selected.value = null;
  hovered.value = null;
}
async function navigateWaypoint(waypoint) {
  selectWaypoint(waypoint);
  await nextTick();
  closeButton.value.focus();
}
const actions = [
  { label: "Center on map", icon: "target", color: "text-[#f1f5f9]" },
  { label: "Copy coordinates", icon: "copy", color: "text-[#f1f5f9]" },
  { label: "Add annotation", icon: "message", color: "text-[#fbbf24]" },
  { label: "Add waypoint after", icon: "plus", color: "text-[#3b82f6]" },
  { label: "Set as start", icon: "pin", color: "text-[#22c55e]" },
  { label: "Set as end", icon: "finish", color: "text-[var(--color-focus)]" },
  {
    label: "Delete waypoint",
    icon: "trash",
    color: "text-[var(--color-error-text)]",
  },
];
</script>

<template>
  <div
    data-product-preview
    role="region"
    aria-label="Interactive route preview with a turn and bridge structure"
    class="bridge-preview relative overflow-hidden rounded-2xl border border-white/10 bg-[var(--bg-secondary)] shadow-2xl shadow-black/20"
    @keydown.esc="clearSelection"
  >
    <div class="border-b border-white/10 px-4 py-3 text-sm font-semibold">
      <div class="flex items-center gap-2">
        <UiIcon name="map" :size="18" /> Route Map
      </div>
      <p
        class="mt-1 text-xs font-normal leading-5 text-[var(--color-text-muted)]"
      >
        <span class="bridge-preview-hint-hover"
          >Hover for details. Click a waypoint for options.</span
        >
        <span class="bridge-preview-hint-touch"
          >Tap a waypoint for options.</span
        >
      </p>
    </div>
    <div
      data-home-preview-map
      class="bridge-preview-map relative overflow-hidden bg-white"
      @click="clearSelection"
    >
      <picture>
        <source
          media="(max-width: 639px)"
          srcset="/examples/fm93-bridge-mobile.jpg"
          width="800"
          height="1100"
        />
        <img
          src="/examples/fm93-bridge-desktop.jpg"
          alt="Example route turning from Hubbard Lane onto FM 93 and crossing the Leon River in Texas"
          width="1600"
          height="1300"
          loading="lazy"
          class="block h-auto w-full"
        />
      </picture>
      <button
        v-for="waypoint in waypoints"
        :key="waypoint.type"
        type="button"
        :data-preview-marker="waypoint.type"
        :aria-label="`Show ${waypoint.label}`"
        :aria-pressed="selected === waypoint"
        aria-controls="home-waypoint-menu"
        :aria-expanded="selected === waypoint"
        :aria-describedby="
          hovered === waypoint ? 'home-waypoint-tooltip' : undefined
        "
        :style="waypoint.position"
        class="bridge-preview-marker absolute flex h-11 w-11 items-center justify-center rounded-full"
        @click.stop="selectWaypoint(waypoint)"
        @mouseenter="hovered = waypoint"
        @mouseleave="hovered = null"
        @focus="hovered = waypoint"
        @blur="hovered = null"
      >
        <span
          aria-hidden="true"
          class="bridge-preview-symbol"
          :class="`bridge-preview-symbol--${waypoint.type}`"
          :style="{
            backgroundColor: waypoint.marker.color,
            width: `${waypoint.marker.size}px`,
            height: `${waypoint.marker.size}px`,
          }"
          >{{ waypoint.marker.label }}</span
        >
      </button>
      <div
        v-if="hovered"
        id="home-waypoint-tooltip"
        role="tooltip"
        :style="hovered.position"
        :class="{
          'bridge-preview-tooltip--bridge': hovered.type === 'bridge',
        }"
        class="bridge-preview-tooltip absolute rounded border px-2 py-1.5 shadow-xl"
      >
        <strong class="flex items-center gap-1 font-bold"
          ><UiIcon
            v-if="hovered.type === 'bridge'"
            name="warning"
            :size="13"
          />{{ hovered.label }}</strong
        >
        <span class="block text-[#aaa]">{{ hovered.typeLabel }}</span>
        <span v-if="hovered.comment" class="block text-[#fbbf24]"
          >Note: {{ hovered.comment }}</span
        >
      </div>
      <a
        href="https://www.openstreetmap.org/copyright"
        class="absolute bottom-0 right-0 bg-white/90 px-1 text-xs leading-5 text-[#0078a8]"
        >© OpenStreetMap contributors</a
      >
      <section
        v-if="selected"
        id="home-waypoint-menu"
        :style="selected.position"
        :class="`waypoint-preview-menu--${selected.type}`"
        :aria-label="`${selected.typeLabel} waypoint details preview`"
        class="waypoint-preview-menu overflow-auto rounded-xl border border-[#334155] bg-[#1e293b] text-xs shadow-2xl"
        @click.stop
      >
        <div class="border-b border-[#334155] p-3">
          <div class="flex items-center justify-between">
            <h3 class="truncate pr-2 text-sm font-bold text-white">
              {{ selected.label }}
            </h3>
            <button
              ref="closeButton"
              type="button"
              aria-label="Close waypoint details"
              class="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-xl text-[#94a3b8] hover:bg-[#334155] hover:text-white"
              @click="clearSelection"
            >
              ×
            </button>
          </div>
          <p class="mt-0.5 text-xs text-[#94a3b8]">
            {{ selected.typeLabel
            }}<span v-if="selected.type === 'turn'">
              · 31.04733, -97.43683</span
            >
          </p>
          <p v-if="selected.comment" class="mt-1 text-xs italic text-[#fbbf24]">
            {{ selected.comment }}
          </p>
        </div>
        <div class="space-y-0.5 p-1.5">
          <div
            aria-label="Waypoint navigation preview"
            class="flex items-center gap-1 text-xs"
          >
            <span class="px-2 py-2 text-[#22c55e]">S</span>
            <button
              type="button"
              :disabled="selected.type === 'turn'"
              class="flex-1 rounded-lg py-2 text-center text-[#f1f5f9]"
              @click="navigateWaypoint(waypoints[0])"
            >
              ◀ Prev
            </button>
            <span class="min-w-9 text-center text-[#94a3b8]"
              >{{ selected.type === "turn" ? 2 : 3 }}/4</span
            >
            <button
              type="button"
              class="flex-1 rounded-lg py-2 text-center text-[#f1f5f9] hover:bg-[#334155]"
              :disabled="selected.type === 'bridge'"
              @click="navigateWaypoint(waypoints[1])"
            >
              Next ▶
            </button>
            <span class="px-2 py-2 text-[var(--color-error-text)]">E</span>
          </div>
          <BridgeDialogNavigation
            :current-index="selected.type === 'bridge' ? 0 : -1"
            :count="1"
            @next="navigateWaypoint(waypoints[1])"
          />
          <span
            v-for="action in actions"
            :key="action.label"
            class="flex items-center gap-2.5 rounded-lg px-3 py-2 text-xs"
            :class="action.color"
            ><UiIcon :name="action.icon" :size="16" />{{ action.label }}</span
          >
        </div>
      </section>
    </div>
    <div
      class="border-t border-white/10 px-4 py-2 text-center text-xs text-[var(--color-text-muted)]"
    >
      Product preview · Real bridge · Fictional permit
    </div>
  </div>
</template>

<style scoped>
.bridge-preview {
  container: bridge-preview / inline-size;
}
.bridge-preview-hint-touch {
  display: none;
}
@media (hover: none) {
  .bridge-preview-hint-hover {
    display: none;
  }
  .bridge-preview-hint-touch {
    display: inline;
  }
}
.waypoint-preview-menu {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 288px;
  max-width: calc(100% - 24px);
  max-height: calc(100% - 24px);
  z-index: 1;
}
.waypoint-preview-menu--turn {
  left: calc(var(--x) * 1% + 24px);
}
.waypoint-preview-menu--bridge {
  right: calc(100% - var(--x) * 1% + 24px);
}
.waypoint-preview-menu button:focus-visible {
  outline: 2px solid var(--color-focus);
  outline-offset: -2px;
}
.bridge-preview-map {
  container-type: inline-size;
  aspect-ratio: 800 / 650;
}
.bridge-preview-map picture {
  position: absolute;
  inset: 0;
}
.bridge-preview-map picture img {
  width: 100%;
  height: 100%;
}
.bridge-preview-marker,
.bridge-preview-tooltip,
.waypoint-preview-menu {
  --x: var(--desktop-x);
  --y: var(--desktop-y);
}
.bridge-preview-marker {
  left: calc(var(--x) * 1%);
  top: calc(var(--y) * 1%);
  transform: translate(-50%, -50%);
  cursor: pointer;
}
.bridge-preview-marker:focus-visible {
  outline: 3px solid #2563eb;
  outline-offset: 2px;
}
.bridge-preview-tooltip {
  --tooltip-width: 160px;
  --tooltip-left: clamp(
    12px,
    calc(var(--x) * 1cqw - var(--tooltip-width) / 2),
    calc(100cqw - var(--tooltip-width) - 12px)
  );
  left: var(--tooltip-left);
  bottom: calc(100% - var(--y) * 1% + 18px);
  width: var(--tooltip-width);
  max-width: calc(100% - 24px);
  font-size: var(--font-size-caption);
  line-height: var(--line-height-caption);
  color: var(--color-text-primary);
  background: var(--color-surface-raised);
  border-color: var(--color-border-strong);
  pointer-events: none;
}
.bridge-preview-tooltip--bridge {
  --tooltip-width: 360px;
}
.bridge-preview-symbol {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid white;
  color: white;
  font-size: var(--font-size-caption);
  line-height: var(--line-height-caption);
  font-weight: bold;
}
.bridge-preview-symbol--turn {
  border-radius: 50%;
  box-shadow:
    0 0 0 2px #dc2626,
    0 2px 8px rgb(220 38 38 / 60%);
}
.bridge-preview-symbol--bridge {
  transform: rotate(45deg);
  box-shadow: 0 2px 6px rgb(0 0 0 / 40%);
}
.bridge-preview-tooltip::after {
  content: "";
  position: absolute;
  top: 100%;
  left: calc(var(--x) * 1cqw - var(--tooltip-left) - 6px);
  border: 6px solid transparent;
  border-top-color: var(--color-surface-raised);
}
@media (max-width: 639px) {
  .bridge-preview-map {
    aspect-ratio: 400 / 550;
  }
  .bridge-preview-marker,
  .bridge-preview-tooltip,
  .waypoint-preview-menu {
    --x: var(--mobile-x);
    --y: var(--mobile-y);
  }
  .waypoint-preview-menu {
    left: clamp(12px, calc(var(--x) * 1% - 144px), calc(100% - 300px));
    right: auto;
    transform: none;
    top: calc(60% + 24px);
    max-height: calc(40% - 36px);
  }
}
</style>
